import { Treatment } from "@/services/treatments/treatmentsAPI";
import {
    AdHocFactorTypeDTO,
    ApplicationFactorDTO,
    GlobalRegistryFactorTypeDTO,
    GlobalRegistryFactorTypeWithFactorsDTO,
    SavedAdHocFactor,
    SavedAdHocFactorType,
    SavedApplicationFactor,
    SavedFactor,
    SavedGlobalRegistryFactor,
    SavedGlobalRegistryFactorDTO,
} from "@/services/conditions/conditionFactors/conditionFactorsAPI";
import { isEqualConditionAssociations, UpsertCondition } from "@/services/conditions/conditionsAPI";
import { omitFromArray } from "@/lib/objectUtils";
import { RegistryEntry } from "@/services/registries/registriesAPI";
import { permute } from "@/lib/arrayUtils";
import _ from "lodash";

export type SelectionsByFactorId = {
    factorIdSelections: Record<string, boolean>;
};

export type FactorSelections = {
    applicationFactorIdSelections: Record<string, boolean>;
    factorIdSelectionsByAdHocFactorType: Record<string, SelectionsByFactorId>;
    factorIdSelectionsByGlobalRegistryFactorType: Record<string, SelectionsByFactorId>;
};

export type BuildUpsertConditionsResult = {
    upsertConditions: UpsertCondition[];
    treatments: Treatment[];
    adHocFactorTypes: SavedAdHocFactorType[];
    globalRegistryFactorTypes: GlobalRegistryFactorTypeDTO[];
};

export function buildFactorSelectionsByTreatmentId(
    existingConditions: UpsertCondition[],
    treatments: Treatment[],
    applicationFactors: ApplicationFactorDTO[],
    adHocFactorTypeDTOs: AdHocFactorTypeDTO[],
    globalRegistryFactorTypes: GlobalRegistryFactorTypeWithFactorsDTO[]
) {
    const builder = new FactorSelectionBuilder(
        existingConditions,
        treatments,
        applicationFactors,
        adHocFactorTypeDTOs,
        globalRegistryFactorTypes
    );

    return builder.buildFactorSelectionsByTreatmentId();
}

class FactorSelectionBuilder {
    constructor(
        public existingConditions: UpsertCondition[],
        public treatments: Treatment[],
        public applicationFactors: ApplicationFactorDTO[],
        public adHocFactorTypeDTOs: AdHocFactorTypeDTO[],
        public globalRegistryFactorTypes: GlobalRegistryFactorTypeWithFactorsDTO[]
    ) {}

    buildFactorSelectionsByTreatmentId() {
        const factorSelectionsByTreatmentId: Record<string, FactorSelections> = {};
        for (const treatment of this.treatments) {
            const isSelected = this.buildIsFactorSelectedFn(treatment);

            factorSelectionsByTreatmentId[treatment.id] =
                this.buildFactorIdSelectionsForTreatment(isSelected);
        }

        return factorSelectionsByTreatmentId;
    }

    private buildIsFactorSelectedFn(treatment: Treatment) {
        if (this.isInitialConditionsSetup()) {
            // if no existing conditions, default all factors checked
            return {
                isApplicationFactorSelected: (_factor: SavedApplicationFactor) => true,
                isAdHocFactorSelected: (_factor: SavedAdHocFactor) => true,
                isGlobalRegistryFactorSelected: (_factor: SavedGlobalRegistryFactor) => true,
            };
        }

        const conditionForTreatments = this.existingConditions.filter((condition) => {
            return condition.treatment.id === treatment.id;
        });

        const isApplicationFactorSelected = (factor: SavedApplicationFactor) => {
            return this.isFactorSelected(
                conditionForTreatments,
                (condition) => condition.applicationFactors,
                factor
            );
        };

        const isAdHocFactorSelected = (factor: SavedAdHocFactor) => {
            return this.isFactorSelected(
                conditionForTreatments,
                (condition) => condition.adHocFactors,
                factor
            );
        };

        const isGlobalRegistryFactorSelected = (factor: SavedGlobalRegistryFactor) => {
            return this.isFactorSelected(
                conditionForTreatments,
                (condition) => condition.globalRegistryFactors,
                factor
            );
        };

        return {
            isApplicationFactorSelected,
            isAdHocFactorSelected,
            isGlobalRegistryFactorSelected,
        };
    }

    private isInitialConditionsSetup() {
        return this.existingConditions.filter((c) => !_.isNil(c.id)).length === 0;
    }

    private buildFactorIdSelectionsForTreatment(params: {
        isApplicationFactorSelected: (factor: SavedApplicationFactor) => boolean;
        isAdHocFactorSelected: (factor: SavedAdHocFactor) => boolean;
        isGlobalRegistryFactorSelected: (factor: SavedGlobalRegistryFactor) => boolean;
    }) {
        const {
            isApplicationFactorSelected,
            isAdHocFactorSelected,
            isGlobalRegistryFactorSelected,
        } = params;

        const factorIdsForTreatment: FactorSelections = {
            applicationFactorIdSelections: {},
            factorIdSelectionsByAdHocFactorType: {},
            factorIdSelectionsByGlobalRegistryFactorType: {},
        };

        for (const applicationFactor of this.applicationFactors) {
            factorIdsForTreatment.applicationFactorIdSelections[applicationFactor.id] =
                isApplicationFactorSelected(applicationFactor);
        }

        for (const adHocFactorTypeDTO of this.adHocFactorTypeDTOs) {
            const factorIdSelections: Record<string, boolean> = {};
            for (const adHocFactor of adHocFactorTypeDTO.adHocFactors) {
                factorIdSelections[adHocFactor.id] = isAdHocFactorSelected(adHocFactor);
            }
            factorIdsForTreatment.factorIdSelectionsByAdHocFactorType[adHocFactorTypeDTO.id] = {
                factorIdSelections,
            };
        }

        for (const globalRegistryFactorTypeDTO of this.globalRegistryFactorTypes) {
            const factorIdSelections: Record<string, boolean> = {};
            for (const globalRegistryFactor of globalRegistryFactorTypeDTO.globalRegistryFactors) {
                factorIdSelections[globalRegistryFactor.id] =
                    isGlobalRegistryFactorSelected(globalRegistryFactor);
            }
            factorIdsForTreatment.factorIdSelectionsByGlobalRegistryFactorType[
                globalRegistryFactorTypeDTO.id
            ] = {
                factorIdSelections,
            };
        }

        return factorIdsForTreatment;
    }

    private isFactorSelected<T extends SavedFactor>(
        conditionForTreatments: UpsertCondition[],
        conditionFactorExtractorFn: (condition: UpsertCondition) => T[],
        factor: T
    ): boolean {
        if (this.existingConditions.length === 0) {
            return true;
        }

        const found = conditionForTreatments
            .map((condition) => conditionFactorExtractorFn(condition))
            .flat()
            .find((conditionFactor) => factor.id === conditionFactor.id);

        return !_.isNil(found);
    }
}

export function buildUpsertConditions(
    conditions: UpsertCondition[],
    treatments: Treatment[],
    applicationFactors: ApplicationFactorDTO[],
    adHocFactorTypes: AdHocFactorTypeDTO[],
    globalRegistryFactorTypes: GlobalRegistryFactorTypeWithFactorsDTO[],
    selectedTreatmentFactorIdsByTreatmentId: Record<string, FactorSelections>
): BuildUpsertConditionsResult {
    const conditionBuilder = new ConditionsBuilder(
        conditions,
        treatments,
        applicationFactors,
        adHocFactorTypes,
        globalRegistryFactorTypes,
        selectedTreatmentFactorIdsByTreatmentId
    );

    const upsertConditions = conditionBuilder.buildConditions();
    return {
        upsertConditions,
        treatments,
        adHocFactorTypes: omitFromArray(adHocFactorTypes, "adHocFactors"),
        globalRegistryFactorTypes: omitFromArray(
            globalRegistryFactorTypes,
            "globalRegistryFactors"
        ),
    };
}

class ConditionFactorBuilder {
    private applicationFactor: ApplicationFactorDTO | null = null;
    private adHocFactors: SavedAdHocFactor[] = [];
    private globalRegistryFactors: SavedGlobalRegistryFactorDTO[] = [];
    private nameBuilderFns: (() => string)[] = [];

    constructor(private treatment: Treatment) {
        this.nameBuilderFns.push(() => {
            return treatment.name;
        });
    }

    addApplicationFactor(applicationFactor: ApplicationFactorDTO): void {
        this.applicationFactor = applicationFactor;
        this.nameBuilderFns.push(() => {
            return applicationFactor.name;
        });
    }

    addAdHocFactor(adHocFactor: SavedAdHocFactor): void {
        this.adHocFactors.push(adHocFactor);
        this.nameBuilderFns.push(() => {
            return adHocFactor.value;
        });
    }

    addGlobalRegistryFactor(
        globalRegistryFactor: SavedGlobalRegistryFactor,
        registryEntry: RegistryEntry
    ): void {
        this.globalRegistryFactors.push({
            ...globalRegistryFactor,
            registryEntry,
        });
        this.nameBuilderFns.push(() => {
            return registryEntry.displayName;
        });
    }

    buildUpsertCondition(number: number): UpsertCondition {
        return {
            id: null,
            name: this.buildName(),
            number,
            treatment: this.treatment,
            applicationFactors: this.applicationFactor ? [this.applicationFactor] : [],
            adHocFactors: this.adHocFactors,
            globalRegistryFactors: this.globalRegistryFactors,
        };
    }

    buildName() {
        return this.nameBuilderFns.map((fn) => fn()).join(" ");
    }
}

/**
 * Add a specific factor to the given ConditionFactorBuilder. This exists so we can
 * permute the various combinations of factors regardless of what kind of factor
 * (application, ad hoc, registry) they are.
 */
type ConditionFactorCollector = {
    /**
     * The ID of the factor to add to the ConditionFactorBuilder. Used to filter
     * the factors the user selected for each treatment - not all factors should
     * be assigned to each treatment.
     */
    factorId: string;

    /**
     * Add the specific factor to the given FactorConditionBuilder
     */
    addFactor: (conditionBuilder: ConditionFactorBuilder) => void;
};

class ConditionsBuilder {
    private readonly allConditionFactorBuilderCollectors: ConditionFactorCollector[][];
    private readonly allSelectedFactorIdsByTreatmentId: Record<string, string[]>;

    constructor(
        private existingConditions: UpsertCondition[],
        private treatments: Treatment[],
        private applicationFactors: ApplicationFactorDTO[],
        private adHocFactorTypes: AdHocFactorTypeDTO[],
        private globalRegistryFactorTypes: GlobalRegistryFactorTypeWithFactorsDTO[],
        selectedTreatmentFactorIdsByTreatmentId: Record<string, FactorSelections>
    ) {
        this.allConditionFactorBuilderCollectors = [
            this.buildApplicationFactorConditionBuilderFns(),
            ...this.buildAdHocFactorConditionBuilderGroup(),
            ...this.buildGlobalRegistryFactorConditionBuilderGroup(),
        ];
        this.allSelectedFactorIdsByTreatmentId = flattenSelectedFactorIds(
            selectedTreatmentFactorIdsByTreatmentId
        );
    }

    buildConditions() {
        const allUpsertConditions: UpsertCondition[] = [];

        let conditionNumber = 0;

        function nextConditionNumber() {
            conditionNumber++;
            return conditionNumber;
        }

        for (const treatment of this.treatments) {
            const conditionFactorBuilderGroupsForTreatment: ConditionFactorCollector[][] =
                this.getConditionFactorCollectorGroupsForTreatment(treatment);

            const permuted = permute(conditionFactorBuilderGroupsForTreatment);

            const upsertConditions = this.buildConditionsForTreatment(
                treatment,
                permuted,
                nextConditionNumber
            );
            allUpsertConditions.push(...upsertConditions);
        }

        return allUpsertConditions;
    }

    /**
     * Return the ConditionFactorCollectors that apply to the given Treatment based
     * on which factors the user selected for that treatment
     */
    private getConditionFactorCollectorGroupsForTreatment(
        treatment: Treatment
    ): ConditionFactorCollector[][] {
        const allCollectors: ConditionFactorCollector[][] = [];
        for (const collectors of this.allConditionFactorBuilderCollectors) {
            const selectedCollectors = collectors.filter((collector) => {
                return this.isSelectedFactorForTreatment(treatment, collector);
            });

            if (selectedCollectors.length > 0) {
                allCollectors.push(selectedCollectors);
            }
        }

        return allCollectors;
    }

    /**
     * Returns true if the given ConditionFactorCollector's factor was selected by
     * the user for the given Treatment
     */
    private isSelectedFactorForTreatment(
        treatment: Treatment,
        collector: ConditionFactorCollector
    ): boolean {
        const selectedTreatmentFactorIds = this.allSelectedFactorIdsByTreatmentId[treatment.id];
        return selectedTreatmentFactorIds.includes(collector.factorId);
    }

    private buildConditionsForTreatment(
        treatment: Treatment,
        conditionFactorBuilderGroups: ConditionFactorCollector[][],
        nextConditionNumber: () => number
    ): UpsertCondition[] {
        return conditionFactorBuilderGroups.map((collectorsForFactorType) => {
            const conditionFactorBuilder = new ConditionFactorBuilder(treatment);
            collectorsForFactorType.forEach((collector) => {
                collector.addFactor(conditionFactorBuilder);
            });

            const conditionNumber = nextConditionNumber();
            const builtCondition = conditionFactorBuilder.buildUpsertCondition(conditionNumber);

            const existing = this.findExistingCondition(builtCondition);
            if (
                existing &&
                existing.name &&
                existing.name.trim().length > 0 &&
                existing.name != builtCondition.name
            ) {
                builtCondition.name = existing.name;
            }

            return builtCondition;
        });
    }

    private findExistingCondition(condition: UpsertCondition) {
        return this.existingConditions.find((existingCondition) => {
            return isEqualConditionAssociations(condition, existingCondition);
        });
    }

    private buildApplicationFactorConditionBuilderFns(): ConditionFactorCollector[] {
        return this.applicationFactors.map((factor) => {
            function addFactor(conditionBuilder: ConditionFactorBuilder) {
                conditionBuilder.addApplicationFactor(factor);
            }

            return {
                addFactor,
                factorId: factor.id,
            };
        });
    }

    private buildAdHocFactorConditionBuilderGroup(): ConditionFactorCollector[][] {
        return this.adHocFactorTypes.map((adHocFactorType) => {
            return buildAdHocFactorConditionBuilderGroup(adHocFactorType);
        });
    }

    private buildGlobalRegistryFactorConditionBuilderGroup(): ConditionFactorCollector[][] {
        return this.globalRegistryFactorTypes.map((globalRegistryFactorType) => {
            return buildGlobalRegistryFactorBuilderGroup(globalRegistryFactorType);
        });
    }
}

function buildAdHocFactorConditionBuilderGroup(
    adHocFactorType: AdHocFactorTypeDTO
): ConditionFactorCollector[] {
    return adHocFactorType.adHocFactors.map((adHocFactor) => {
        return buildAdHocFactorConditionBuilderFn(adHocFactor);
    });
}

function buildAdHocFactorConditionBuilderFn(
    adHocFactor: SavedAdHocFactor
): ConditionFactorCollector {
    function addFactor(conditionBuilder: ConditionFactorBuilder) {
        conditionBuilder.addAdHocFactor(adHocFactor);
    }

    return {
        addFactor,
        factorId: adHocFactor.id,
    };
}

function buildGlobalRegistryFactorBuilderGroup(
    globalRegistryFactorType: GlobalRegistryFactorTypeWithFactorsDTO
): ConditionFactorCollector[] {
    return globalRegistryFactorType.globalRegistryFactors.map((globalRegistryFactor) => {
        const entry = getGlobalRegistryEntryForFactor(
            globalRegistryFactorType,
            globalRegistryFactor
        );
        return buildGlobalRegistryFactorConditionBuilderFn(globalRegistryFactor, entry);
    });
}

function buildGlobalRegistryFactorConditionBuilderFn(
    globalRegistryFactor: SavedGlobalRegistryFactor,
    entry: RegistryEntry
): ConditionFactorCollector {
    function addFactor(conditionBuilder: ConditionFactorBuilder) {
        conditionBuilder.addGlobalRegistryFactor(globalRegistryFactor, entry);
    }

    return {
        addFactor,
        factorId: globalRegistryFactor.id,
    };
}

function getGlobalRegistryEntryForFactor(
    globalRegistryFactorType: GlobalRegistryFactorTypeWithFactorsDTO,
    globalRegistryFactor: SavedGlobalRegistryFactor
) {
    const id = globalRegistryFactor.globalRegistryEntryId;
    const entry = globalRegistryFactorType.globalRegistry.registryEntries.find(
        (entry) => entry.id === id
    );
    if (!entry) {
        throw new Error(
            `Could not find registry entry id ${id} in ${globalRegistryFactorType.globalRegistry.displayName}`
        );
    }

    return entry;
}

function flattenSelectedFactorIds(
    selectedTreatmentFactorIdsByTreatmentId: Record<string, FactorSelections>
): Record<string, string[]> {
    const treatmentIds = Object.keys(selectedTreatmentFactorIdsByTreatmentId);
    const selectedFactorsByTreatmentId: Record<string, string[]> = {};
    for (const treatmentId of treatmentIds) {
        const factorSelections = selectedTreatmentFactorIdsByTreatmentId[treatmentId];
        selectedFactorsByTreatmentId[treatmentId] =
            flattenSelectedFactorIdsForTreatment(factorSelections);
    }

    return selectedFactorsByTreatmentId;
}

function flattenSelectedFactorIdsForTreatment(factorSelections: FactorSelections): string[] {
    const selectedApplicationFactorIds = getSelectedFactorIds(
        factorSelections.applicationFactorIdSelections
    );
    const selectedAdHocFactorIds = collectAllSelectedFactorIds(
        factorSelections.factorIdSelectionsByAdHocFactorType
    );
    const selectedGlobalRegistryFactorIds = collectAllSelectedFactorIds(
        factorSelections.factorIdSelectionsByGlobalRegistryFactorType
    );

    return [
        ...selectedApplicationFactorIds,
        ...selectedAdHocFactorIds,
        ...selectedGlobalRegistryFactorIds,
    ];
}

function collectAllSelectedFactorIds(
    factorIdSelectionsByTypeId: Record<string, SelectionsByFactorId>
): string[] {
    const allSelectionsByFactorId = Object.values(factorIdSelectionsByTypeId);
    const allSelectedFactorIds: string[] = [];
    for (const selectionsByFactorId of allSelectionsByFactorId) {
        const selectedFactorIds = getSelectedFactorIds(selectionsByFactorId.factorIdSelections);
        allSelectedFactorIds.push(...selectedFactorIds);
    }

    return allSelectedFactorIds;
}

function getSelectedFactorIds(factorIdSelections: Record<string, boolean>): string[] {
    const allFactorIds = Object.keys(factorIdSelections);
    return allFactorIds.filter((factorId) => factorIdSelections[factorId]);
}
